import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'

import { settings, translate } from 'parking-react-common'
import {
    map, get, reverse, sortBy as _sortBy, flatten,
    toLower, toArray,
    has, isEqual, isString, isEmpty, isNumber
} from 'lodash'

import { api, routing, ui, search, filters } from '../redux'

import Panel from '../components/Panel'
import Table from '../components/Table'
import Button from '../components/Button'
import SearchInput from '../components/SearchInput'
import FiltersPanel from '../components/FiltersPanel'
import FiltersButton from '../components/FiltersButton'
import ActiveFiltersList from '../components/ActiveFiltersList'
import DropdownMenu from '../components/DropdownMenu'
import DropdownMenuItem from '../components/DropdownMenuItem'
import NavBar from '../components/NavBar'

class List extends Component {
    componentDidMount() {
        const { filteredDataURL, filterKeys, dispatch } = this.props
        dispatch(api.get(filteredDataURL))
        dispatch(filters.enable(filterKeys))
    }

    componentWillReceiveProps(nextProps) {
        if (!isEqual(get(nextProps, 'filteredDataURL'), get(this.props, 'filteredDataURL'))) {
            const { filteredDataURL, dispatch } = nextProps
            dispatch(api.get(filteredDataURL))
        }

        if (!isEqual(get(nextProps, 'filterKeys'), get(this.props, 'filterKeys'))) {
            const { filterKeys, dispatch } = nextProps
            dispatch(filters.enable(filterKeys))
        }

        if (!isEqual(get(nextProps, 'rawData'), get(this.props, 'rawData'))) {
            const { queryFromAddressBar } = nextProps
            if (queryFromAddressBar) {
                this.performSearch(queryFromAddressBar)
            }
        }
    }

    handleDownload(format) {
        const { dispatch, dataURL, title, data } = this.props
        const payload = {
            data   : { ids: map(data, 'id') },
            method : 'POST'
        }
        dispatch(api.download(dataURL, { id: 'download', format }, `${title}.${format}`, payload))
        dispatch(ui.toggleMenu('downloadFormats', ui.MENUS.dropdown))
    }

    performSearch(query) {
        const { dataURL, searchKeys, dataSelector, dispatch } = this.props
        dispatch(search.perform({
            query,
            url      : dataURL,
            keys     : searchKeys,
            selector : dataSelector,
            options  : { updateAddressBar: true }
        }))
    }

    render() {
        const {
            title, subtitle, columns, data, dataURL, loadMoreURL, searchQuery, filterKeys, tools,
            onRowClick, defaultPageSize, nextPageNumber, dispatch, t, isLoading, isSearchable,
            isSearching, isSearchPerformed, isDownloadable, isMassDownloadable, isFiltrable, isFiltered
        } = this.props

        const canLoadMore = !isEmpty(data) && isNumber(nextPageNumber) && !searchQuery

        return (
            <div>
                <NavBar title={ title } />
                <Panel
                    hasTable
                    title={ subtitle }
                    tools={ [
                        ...flatten([tools]),
                        isSearchable && (
                            <SearchInput
                                size={ ui.SIZE.small }
                                value={ searchQuery }
                                isSearching={ isSearching }
                                onChange={ (event) => this.performSearch(event.target.value) }
                                placeholder={
                                    (isString(subtitle) && t('Search for {{type}}...', { type: toLower(subtitle) }))
                                    || (isString(title) && t('Search for {{type}}...', { type: toLower(title) }))
                                    || null
                                }
                            />
                        ),
                        isFiltrable && <FiltersButton />
                    ] }
                    heading={ isFiltrable && (
                        <Fragment>
                            <ActiveFiltersList items={ filterKeys } />
                            <FiltersPanel items={ filterKeys } />
                        </Fragment>
                    ) }
                >
                    <Table
                        columns={ columns }
                        data={ data }
                        withBorders={ false }
                        isLoading={ isLoading }
                        noDataText={
                            isFiltered
                                ? t('Nothing found for the applied filters.')
                                : isSearchPerformed
                                    ? t('Nothing found for "{{query}}".', { query: searchQuery })
                                    : t('No data available.')
                        }
                        onRowClick={ onRowClick }
                    />
                    <div className="row be-datatable-footer">
                        { canLoadMore && (
                            <div className="xs-p-15" style={ { float: 'left' } }>
                                <Button
                                    label={ t('Load More') }
                                    onClick={ () => dispatch(api.get(loadMoreURL, {
                                        page_size : defaultPageSize,
                                        page      : nextPageNumber
                                    })) }
                                    disabled={ isLoading }
                                />
                            </div>
                        ) }
                        { isDownloadable && (
                            <div className="xs-p-15" style={ { float: 'right' } }>
                                <DropdownMenu
                                    name="downloadFormats"
                                    trigger={ (
                                        <Button
                                            label={ t('Download') }
                                            disabled={ isEmpty(data) }
                                            isDropdown
                                        />
                                    ) }
                                >
                                    <DropdownMenuItem
                                        label={ t('PDF') }
                                        onClick={ () => this.handleDownload('pdf') }
                                    />
                                    <DropdownMenuItem
                                        label={ t('XLS') }
                                        onClick={ () => this.handleDownload('xlsx') }
                                    />
                                    <DropdownMenuItem
                                        label={ t('CSV') }
                                        onClick={ () => this.handleDownload('csv') }
                                    />
                                </DropdownMenu>
                            </div>
                        ) }
                        { isMassDownloadable && (
                            <div className="xs-p-15" style={ { float: 'right' } }>
                                <Button
                                    label={ t('Download All') }
                                    onClick={ () => dispatch({
                                        type    : api.MASS_DOWNLOAD_REQUESTED,
                                        payload : { dataURL, data }
                                    }) }
                                    disabled={ isEmpty(data) }
                                />
                            </div>
                        )}
                    </div>
                </Panel>
            </div>
        )
    }
}

List.propTypes = {
    title               : PropTypes.node,
    subtitle            : PropTypes.node,
    columns             : PropTypes.arrayOf(PropTypes.object),
    data                : PropTypes.arrayOf(PropTypes.object),
    dataURL             : PropTypes.string,
    filteredDataURL     : PropTypes.string,
    loadMoreURL         : PropTypes.string,
    dataSelector        : PropTypes.func,
    searchQuery         : PropTypes.string,
    searchKeys          : PropTypes.arrayOf(PropTypes.string),
    filterKeys          : PropTypes.object,
    tools               : PropTypes.node,
    onRowClick          : PropTypes.func,
    defaultPageSize     : PropTypes.number,
    nextPageNumber      : PropTypes.number,
    sortBy              : PropTypes.string,                    // eslint-disable-line
    sortDirection       : PropTypes.oneOf(['asc', 'desc']),    // eslint-disable-line
    isLoading           : PropTypes.bool,
    isSearchable        : PropTypes.bool,
    isSearching         : PropTypes.bool,
    isSearchPerformed   : PropTypes.bool,
    isDownloadable      : PropTypes.bool,
    isMassDownloadable  : PropTypes.bool,
    isFiltrable         : PropTypes.bool,
    isFiltered          : PropTypes.bool,
    queryFromAddressBar : PropTypes.string,
    dispatch            : PropTypes.func
}

List.defaultProps = {
    isLoading          : false,
    isSearchable       : true,
    isSearching        : false,
    isDownloadable     : false,
    isMassDownloadable : false
}

const sortData = (data, by, direction) => {
    const sortedData = _sortBy(data, by)
    return direction === 'asc' ? sortedData : reverse(sortedData)
}

function mapStateToProps(state, ownProps) {
    const { dataSelector, dataURL, sortBy, sortDirection, filterKeys } = ownProps

    const searchQuery = search.getQuery(dataURL)(state)
    const searchResults = search.getResults(dataURL)(state)
    const isSearching = search.isSearching(dataURL)(state)
    const isSearchPerformed = search.isPerformed(dataURL)(state)
    const searchParams = isSearchPerformed ? { search: searchQuery } : {}

    const isFiltrable = !isEmpty(filterKeys)
    const filtersParams = filters.getActiveParams(state)
    const activeFilters = filters.getActive(state)

    const isFiltered = isFiltrable && !isEmpty(activeFilters)

    const rawData = has(ownProps, 'data')
        ? ownProps.data
        : toArray(dataSelector(state))

    const dataAfterSerach = isSearchPerformed
        ? searchResults
        : rawData

    const dataAfterFilters = isFiltered
        ? filters.apply(dataAfterSerach)(state)
        : dataAfterSerach

    const data = sortData(dataAfterFilters, sortBy, sortDirection)

    const defaultPageSize = settings.DEFAULT_PAGE_SIZE
    const url = api.buildURL(dataURL, { page_size: defaultPageSize, ...filtersParams, ...searchParams })

    const nextPageNumber = api.getNextPageNumber(url)(state)
    const currentPageNumber = api.getCurrentPageNumber(url)(state)
    const loadMoreURL = api.buildURL(url, { page: nextPageNumber })

    const initialPageSize = currentPageNumber * defaultPageSize
    const filteredDataURL = api.buildURL(dataURL, { page_size: initialPageSize, ...filtersParams, ...searchParams })

    const queryFromAddressBar = get(routing.getParams(state), 'search')

    const isLoading = api.isLoading(url)(state) || api.isLoading(loadMoreURL)(state)

    return {
        data,
        rawData,
        searchQuery,
        searchResults,
        initialPageSize,
        defaultPageSize,
        nextPageNumber,
        filteredDataURL,
        loadMoreURL,
        queryFromAddressBar,
        isLoading,
        isSearching,
        isSearchPerformed,
        isFiltrable,
        isFiltered
    }
}

const ReduxList = translate()(connect(mapStateToProps)(List))

ReduxList.defaultProps = {
    sortBy        : 'id',
    sortDirection : 'desc'
}

export default ReduxList
